import { useRef, useState } from 'react';
import Icon from './Icon';
import Sheet from './Sheet';
import { PRIMARY, CreditsNote } from './CaptureSheets';
import { useAiCredits } from '../lib/credits';
import { useAiConfirm } from './AiConfirm';
import { ProBadge } from './ProSheet';
import { MEAL_LABELS, MEAL_SHORT, MEAL_STYLE, MEAL_TYPES, grams, kcal } from '../lib/format';
import { useApp } from '../lib/AppContext';
import { PortionTable } from '../cook/PairSheets';

const FIELD = 'input h-11 w-full rounded-[14px] border-line text-base';
const APP_NAMES = { grabfood: 'GrabFood', shopeefood: 'ShopeeFood' };

// What the user would like instead goes to the AI as a wish; left blank, the AI picks any different dish.
function SwapForm({ meal, busy, onSubmit }) {
  const credits = useAiCredits(1);
  const [confirmAi, confirmSheet] = useAiConfirm('menu_swap', { title: 'Đổi món bằng AI?' });
  const [wish, setWish] = useState('');
  // The confirmation sheet sits outside the form: it has its own form (tap outside to close).
  return (
    <>
    {confirmSheet}
    <form onSubmit={(e) => { e.preventDefault(); if (!credits.blocked(() => onSubmit(wish))) confirmAi(() => onSubmit(wish)); }} className="flex flex-col gap-3">
      <p className="text-sm text-muted">Đang là: <span className="font-semibold text-base-content">{meal.name}</span></p>
      <label className="flex flex-col gap-2">
        <span className="text-[15px] font-semibold">Bạn muốn ăn gì thay vào? <span className="font-normal text-muted">(không bắt buộc)</span></span>
        <textarea className="textarea min-h-28 w-full rounded-[14px] border-line text-base" value={wish} disabled={busy}
          onChange={(e) => setWish(e.target.value)}
          placeholder="VD: món nước có cá, bún hoặc miến, không dùng whey, nấu nhanh dưới 20 phút" />
      </label>
      <p className="text-xs text-muted">Để trống, AI sẽ tự chọn một món khác. Món mới giữ lượng calo và đạm tương tự.</p>
      <CreditsNote text={credits.note} />
      <button type="submit" className={PRIMARY} disabled={busy}>
        {busy ? <><span className="loading loading-spinner loading-sm" /> Đang tìm món khác…</> : 'Gợi ý món khác'}
      </button>
    </form>
    </>
  );
}

function EditForm({ meal, busy, onSubmit }) {
  const [values, setValues] = useState({
    meal_type: meal.meal_type, name: meal.name, description: meal.description, calories: meal.calories, protein_g: meal.protein_g,
  });
  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  return (
    <form className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        const updated = { ...values, calories: Number(values.calories), protein_g: Number(values.protein_g) };
        // Renamed or moved, not changed: the ingredient list (portions, shopping) still fits the dish.
        const same = updated.description === meal.description && updated.calories === meal.calories && updated.protein_g === meal.protein_g;
        onSubmit(same && meal.ingredients ? { ...updated, ingredients: meal.ingredients } : updated);
      }}>
      <div role="radiogroup" aria-label="Bữa" className="grid grid-cols-4 gap-1 rounded-[16px] bg-base-200 p-1">
        {MEAL_TYPES.map((type) => (
          <button key={type} type="button" role="radio" aria-checked={values.meal_type === type}
            onClick={() => setValues((v) => ({ ...v, meal_type: type }))}
            className={`h-11 rounded-[12px] text-sm font-semibold ${values.meal_type === type ? 'bg-neutral text-neutral-content' : ''}`}>
            {MEAL_SHORT[type]}
          </button>
        ))}
      </div>
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold">Tên món</span>
        <input required className={FIELD} value={values.name} onChange={set('name')} />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold">Nguyên liệu, khẩu phần</span>
        <textarea className="textarea w-full rounded-[14px] border-line text-base" value={values.description} onChange={set('description')} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">kcal</span>
          <input required type="number" inputMode="numeric" min="0" className={`${FIELD} tabular`} value={values.calories} onChange={set('calories')} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Đạm (g)</span>
          <input required type="number" inputMode="decimal" min="0" step="any" className={`${FIELD} tabular`} value={values.protein_g} onChange={set('protein_g')} />
        </label>
      </div>
      <button type="submit" className={PRIMARY} disabled={busy}>
        {busy && <span className="loading loading-spinner loading-sm" />} Lưu món
      </button>
    </form>
  );
}

// One planned dish: slot chip and numbers, the name, its ingredients on one line, and "Ghi món này"
// (only on the dish's own day) to log it into Hôm nay. "⋯" edits it by hand, asks the AI for another, or
// (`onMove`) opens Sắp xếp tuần with this dish lifted. `onTakeout` adds "Đặt ngoài" (today's dishes not
// logged yet); a dish ordered out shows its app and the planned dish struck through, and can go back to it.
// `eaten`: what that day's log has in this slot. Once the slot has a meal, it's done: "Đã ghi" when it's this
// dish, else what was eaten instead with the planned dish struck through, and nothing left to log or swap.
// `meal.pair` (Nấu chung): a shared meal shows both portions and logs only yours; `readOnly` (a Free member of a
// cooking pair) keeps "Ghi" but hides every menu change.
export default function PlanMealCard({ meal, canLog, logged, eaten = [], today = false, onLog, onSwap, onSave, onMove, onTakeout, takeoutRef, readOnly = false }) {
  const { editable: canChange, membership, openPaywall } = useApp();
  const editable = canChange && !readOnly;
  const shared = meal.pair?.shared;
  const mine = meal.pair?.totals?.me;
  const [sheet, setSheet] = useState(null); // null | 'menu' | 'edit' | 'swap'
  // Swapping a dish asks the AI, so it's Pro; editing by hand and "Ghi món này" stay free.
  const swapLocked = membership && !membership.pro;
  const openSwap = () => (swapLocked ? (setSheet(null), openPaywall('pro_required')) : setSheet('swap'));
  const [busy, setBusy] = useState(false);
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState(null);
  const [previous, setPrevious] = useState(null); // the dish before the latest change, for undo
  const [rejected, setRejected] = useState([]); // dishes already swapped away, so the AI won't offer them again

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setSheet(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const swap = (reason) => run(async () => {
    const avoid = [...rejected, meal.name];
    await onSwap(reason, avoid);
    setPrevious(meal);
    setRejected(avoid);
  });
  const save = (updated) => run(async () => {
    await onSave(updated);
    setPrevious(meal);
  });
  const undo = async () => {
    setBusy(true);
    try {
      await onSave(previous);
      setPrevious(null);
    } catch (e) {
      window.alert(e.message);
    } finally {
      setBusy(false);
    }
  };
  // A second tap before the button turns busy would log the dish twice.
  const loggingRef = useRef(false);
  const log = async () => {
    if (loggingRef.current) return;
    loggingRef.current = true;
    setLogging(true);
    try {
      await onLog();
    } catch (e) {
      window.alert(e.message);
    } finally {
      loggingRef.current = false;
      setLogging(false);
    }
  };

  const style = MEAL_STYLE[meal.meal_type];
  // An eaten slot can still be edited, but not swapped, ordered out or logged again.
  const done = eaten.length > 0;
  const ateOther = done && !logged;
  const canSwap = editable && !done;
  const sheetTitle = { menu: meal.name, edit: 'Sửa món', swap: 'Đổi món khác' }[sheet] || meal.name;
  const { takeout } = meal;
  const canOrderOut = onTakeout && !takeout && !done;

  return (
    <article className="flex flex-col gap-2 rounded-[20px] bg-base-100 p-4">
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${style.tile}`}>{MEAL_LABELS[meal.meal_type]}</span>
        {shared ? (
          <span className="flex-1"><span className="rounded-full bg-[#17804f] px-2.5 py-1 text-xs font-semibold text-white">Ăn chung</span></span>
        ) : (
          <span className="tabular flex-1 text-sm text-muted">
            {takeout && '≈ '}{kcal(meal.calories)} kcal · <span className="font-semibold text-protein">{grams(meal.protein_g)} đạm</span>
          </span>
        )}
        {editable && (
          <button type="button" className="btn btn-circle btn-ghost size-11" aria-label={`Tùy chọn món ${meal.name}`}
            disabled={busy} onClick={() => setSheet('menu')}>
            <Icon name="dotsH" className="size-6" />
          </button>
        )}
      </div>
      {takeout && (
        <span className="w-fit rounded-full bg-[#17201b] px-3 py-1 text-xs font-bold text-white">Đặt ngoài · {APP_NAMES[takeout.app] || 'app giao đồ ăn'}</span>
      )}
      <h3 className={`text-[17px] font-bold leading-snug ${ateOther ? 'text-muted line-through' : ''}`}>
        {ateOther && <span className="sr-only">Món trong thực đơn, không ăn: </span>}{meal.name}
      </h3>
      {ateOther ? null : takeout ? (
        <p className="truncate text-sm text-muted"><span className="sr-only">Thay cho món: </span><s>{takeout.original.name}</s></p>
      ) : (
        <p className="truncate text-sm text-muted" title={meal.description}>{meal.description}</p>
      )}

      {shared && !ateOther && <PortionTable pair={meal.pair} />}

      {previous && !busy && (
        <p className="text-xs text-muted">
          Đã đổi từ “{previous.name}” · <button type="button" className="font-semibold text-brand-text underline" onClick={undo}>Hoàn tác</button>
        </p>
      )}
      {busy && !sheet && <p className="flex items-center gap-2 text-sm text-muted"><span className="loading loading-spinner loading-xs" /> Đang lưu…</p>}

      {done && (
        <div className="mt-1 flex items-start gap-2 rounded-[14px] bg-[#e3f1e9] px-4 py-2.5 text-sm text-[#17804f]">
          <Icon name="check" className="mt-0.5 size-4 shrink-0" />
          {logged ? (
            <span className="font-bold">{today ? 'Đã ghi vào Hôm nay' : 'Đã ăn món này'}</span>
          ) : (
            <span>
              <b>Đã ăn món khác: </b>
              {eaten.map((m) => `${m.title}${m.calories != null ? ` · ${kcal(m.calories)} kcal` : ''}`).join(', ')}
            </span>
          )}
        </div>
      )}

      {!done && (canLog || editable) && (
        <div className="mt-1 flex flex-wrap gap-2">
          {canLog && (
            <button type="button" onClick={log} disabled={logging}
              className="btn h-11 rounded-[14px] border-2 border-primary bg-base-100 px-4 font-bold text-brand-text">
              {logging ? <span className="loading loading-spinner loading-xs" /> : <Icon name="check" className="size-4" />}
              {shared && mine ? ` Ghi phần của tôi · ${kcal(mine.calories)} kcal` : ' Ghi món này'}
            </button>
          )}
          {canOrderOut && (
            <button ref={takeoutRef} type="button" onClick={onTakeout} disabled={busy}
              className="btn h-11 rounded-[14px] border-line bg-base-100 px-4 font-semibold">
              <Icon name="bag" className="size-4" /> Đặt ngoài
            </button>
          )}
          {canSwap && !takeout && (
            <button type="button" onClick={openSwap} disabled={busy}
              className="btn h-11 rounded-[14px] border-line bg-base-100 px-4 font-semibold">
              <Icon name="refresh" className="size-4" /> Đổi món{swapLocked && <ProBadge />}
            </button>
          )}
        </div>
      )}

      <Sheet open={!!sheet} onClose={() => { setSheet(null); setError(null); }} title={sheetTitle}>
        {sheet === 'menu' && (
          <div className="flex flex-col gap-2">
            <button type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold"
              onClick={() => setSheet('edit')}>
              <Icon name="pencil" className="size-5" /> Sửa món
            </button>
            {canSwap && (
              <button type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold"
                onClick={openSwap}>
                <Icon name="refresh" className="size-5" /> Đổi món khác{swapLocked && <ProBadge />}
              </button>
            )}
            {canOrderOut && (
              <button type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold"
                onClick={() => { setSheet(null); onTakeout(); }}>
                <Icon name="bag" className="size-5" /> Đặt ngoài
              </button>
            )}
            {takeout && editable && (
              <button type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold"
                onClick={() => save({ ...takeout.original, meal_type: meal.meal_type })}>
                <Icon name="refresh" className="size-5" /> Về món cũ: {takeout.original.name}
              </button>
            )}
            {onMove && canSwap && (
              <button type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold"
                onClick={() => { setSheet(null); onMove(); }}>
                <Icon name="arrange" className="size-5" /> Chuyển món này…
              </button>
            )}
          </div>
        )}
        {sheet === 'edit' && <EditForm meal={meal} busy={busy} onSubmit={save} />}
        {sheet === 'swap' && <SwapForm meal={meal} busy={busy} onSubmit={swap} />}
        {error && <p role="alert" className="mt-3 text-sm font-medium text-error">{error}</p>}
      </Sheet>
    </article>
  );
}
